import { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';

import { BxpWebStargateClient } from '@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client';
import personalEmailsDomains from '@/templates/enterprise/contact/enterprise-contact-sales-page/personal-emails-domains.ts';
import { checkFeatureFlagGate } from '@/utils/feature-flags/check-feature-flag';
import { featureFlags } from '@/utils/feature-flags/feature-flag-manifest';
import { getLocalStorage } from '@/utils/localstorage';

import { StreamhubClient } from '../../../../clients/streamhub-client/streamhub-client.ts';

import { getBillingPreviewSubmissionContext } from './get-billing-preview-submission-context';

const STARGATE_BASE_URL =
	process.env['NODE_ENV'] === 'production'
		? 'https://www.atlassian.com/gateway/api'
		: 'https://wac.stg.internal.atlassian.com/gateway/api';

const stargateClient = new BxpWebStargateClient({
	baseUrl: STARGATE_BASE_URL,
});

const streamhubClient = new StreamhubClient();

export const ATLASSIAN_ISOLATED_CLOUD_PRODUCT = 'Atlassian Isolated Cloud';

export const isAtlassianIsolatedCloudContactFormEnabled = () =>
	checkFeatureFlagGate(featureFlags.gwp11663AtlassianIsolatedCloudContactForm);

export const isEmailFormat = (value: string) => {
	if (value) {
		const emailRegex = new RegExp(/^[\w-]+(\.[\w-]+)*@([a-z0-9-]+\.)+[a-z]{2,7}$/);
		return emailRegex.test(value);
	}
	return false;
};

export const isPersonalDomain = (value: string) => {
	if (value) {
		if (!isEmailFormat(value)) {
			return false;
		}
		const emailDomain = value.split('@')[1];
		return personalEmailsDomains.has(String(emailDomain));
	}
	return false;
};

/**
 * Reads the `pls_source` URL parameter, used to track which entry point brought
 * the user to the contact sales form as part of the PLS contact path discovery
 * experiment. Returns `undefined` when the parameter is absent or empty.
 *
 * See TWCG-1533 and the project poster:
 * https://hello.atlassian.net/wiki/spaces/Growth/pages/6411521304/Project+Poster+-+PLS+Contact+path+discovery
 */
export const getPlsSource = (): string | undefined => {
	if (typeof window === 'undefined') {
		return undefined;
	}
	return new URLSearchParams(window.location.search).get('pls_source') || undefined;
};

const submitSuccess = (
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	state: any,
	createAnalyticsEvent: CreateUIAnalyticsEvent,
) => {
	const plsSource = getPlsSource();
	const billingPreviewContext = getBillingPreviewSubmissionContext(window.location.search);
	const submissionUrl = window.location.href;
	const submissionPath = window.location.pathname;

	if (state.status === 200) {
		window.location.href = state.redirect + window.location.search;
	} else {
		state.mainFormContainer.style.display = 'none';
		state.postSubmitMessage.style.display = 'block';
	}
	createAnalyticsEvent({
		event: 'Contact Sales Form',
		action: 'Submitted',
		actionSubject: 'Contact Sales Form',
		eventType: 'ui',
		origin: 'web',
		platform: 'web',
		product: 'marketing',
		source: 'none',
		userIdType: 'atlassianAccount',
		attributes: {
			atlJSVersion: '1.0.0',
			event: 'Submitted',
			eventComponent: 'Contact Sales Form',
			page_path: submissionPath,
			url: submissionUrl,
			...(plsSource ? { pls_source: plsSource } : {}),
			...billingPreviewContext,
		},
	}).fire();
};

const submitFailure = (createAnalyticsEvent: CreateUIAnalyticsEvent) => {
	createAnalyticsEvent({
		event: 'Contact Sales Form',
		action: 'Failed',
		actionSubject: 'Contact Sales Form',
		eventType: 'ui',
		origin: 'web',
		platform: 'web',
		product: 'marketing',
		source: 'none',
		userIdType: 'atlassianAccount',
		attributes: {
			event: 'Failed',
			eventComponent: 'Contact Sales Form',
			page_path: location.pathname,
			url: location.href,
			atlJSVersion: '1.0.0',
		},
	}).fire();
};

const getCookie = (name: string) => {
	const value = `; ${document.cookie}`;
	const parts = value.split(`; ${name}=`);
	if (parts.length === 2) {
		return parts.pop()?.split(';').shift();
	}
	return '';
};

// @ts-expect-error -- untyped legacy function signature
const sendToStreamhub = async (formValues, formType, createAnalyticsEvent) => {
	const currentTime = new Date();
	const random = Math.round(Math.random() * 10000);
	const { locale } = await stargateClient.getConsentConfig();

	const formTypeMap: { [formType: string]: string } = {
		'pricing-quotes': 'Pricing & Quotes',
		'migration-options': 'Migration Options',
		'product-features': 'Product Features',
	};

	const streamHubInfo = {
		gmoiLocale: String(locale),
		formType: formTypeMap[formType],
		formDef: 'contactSales',
		formSubmissionId: random + currentTime.getTime(),
		created_at: currentTime,
		marketoCookieId: getCookie('_mkto_trk') || '',
		anonymousId: getCookie('ajs_anonymous_id')?.replace(/%22/g, '') || '',
		atlassianId: getCookie('ajs_user_id')?.replace(/%22/g, '') || '',
		submitted: currentTime.toString(),
		source_sequence: currentTime.getTime(),
		userAgent: window.navigator.userAgent,
		isIntercomTracking: false,
		intercomAppId: '',
		intercomMapping: {},
	};

	const payload = Object.assign(formValues, streamHubInfo);

	const streamhubEndpoint = 'contactsalesv2';

	await streamhubClient.postObject(payload, streamhubEndpoint, (state) =>
		submitSuccess(state, createAnalyticsEvent),
	);
};

export const submitContactForm = async (
	form: HTMLFormElement,
	formType: string,
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	// eslint-disable-next-line complexity
) => {
	let editionValue = '';

	if (form['edition']) {
		const value = (form['edition'] as string).split(' ')[1];
		if (value) {
			editionValue = value;
		}
	}

	if (form['name']) {
		form['firstName'] = form['name'].split(' ')[0];
		form['lastName'] = form['name'].split(' ').slice(1).join(' ');
	}

	if (!form['company']) {
		const parsedEmail = form['email'].split('@')[1];
		form['company'] = parsedEmail.split('.')[0];
	}

	const marketingContext = getLocalStorage('marketingContext', true);

	let parsedMarketingContext;
	try {
		parsedMarketingContext = marketingContext ? JSON.parse(marketingContext) : null;
	} catch {
		console.error('Failed to parse marketingContext from localStorage');
	}

	const {
		sfdcCampaignId = '',
		source = '',
		medium = '',
		campaign = '',
		content = '',
	} = parsedMarketingContext?.utm || {};

	form['utm_sfdc-campaign_id'] = sfdcCampaignId;
	form['utm_source'] = source;
	form['utm_medium'] = medium;
	form['utm_campaign'] = campaign;
	form['utm_content'] = content;

	const formValues = {
		...form,
		edition: editionValue ? editionValue : undefined,
	};

	try {
		await sendToStreamhub(formValues, formType, createAnalyticsEvent);
	} catch (error) {
		console.error('Failed to submit form', error);
		submitFailure(createAnalyticsEvent);
		throw error;
	}
};

export const getProductPlans = (selectedProduct: string) => {
	if (
		selectedProduct === 'Atlassian Guard' ||
		selectedProduct === 'Bitbucket' ||
		selectedProduct === 'Jira Product Discovery'
	) {
		const isKeynoteEnabled = checkFeatureFlagGate(featureFlags.team26Keynote);
		return [
			{
				name: 'plan',
				label: 'Cloud Standard',
				value: 'Cloud Standard',
			},
			{
				name: 'plan',
				label: 'Cloud Premium',
				value: 'Cloud Premium',
			},
			...(selectedProduct === 'Jira Product Discovery' && isKeynoteEnabled
				? [
						{
							name: 'plan',
							label: 'Cloud Enterprise',
							value: 'Cloud Enterprise',
						},
					]
				: []),
		];
	} else if (selectedProduct === 'Loom') {
		return [
			{
				name: 'plan',
				label: 'Loom Business',
				value: 'Loom Business',
			},
			{
				name: 'plan',
				label: 'Loom Business + AI',
				value: 'Loom Business + AI',
			},
			{
				name: 'plan',
				label: 'Loom Enterprise',
				value: 'Loom Enterprise',
			},
		];
	} else if (
		selectedProduct === 'Jira Cloud Backup and Restore' ||
		selectedProduct === 'Jira Service Management Cloud Backup and Restore' ||
		selectedProduct === 'Confluence Cloud Backup and Restore'
	) {
		// Backup and Restore products have Cloud Premium and Cloud Enterprise
		return [
			{
				name: 'plan',
				label: 'Cloud Premium',
				value: 'Cloud Premium',
			},
			{
				name: 'plan',
				label: 'Cloud Enterprise',
				value: 'Cloud Enterprise',
			},
		];
	} else if (
		selectedProduct === 'Jira Additional Sandbox' ||
		selectedProduct === 'Confluence Additional Sandbox' ||
		selectedProduct === 'Jira Service Management Additional Sandbox' ||
		selectedProduct === 'Jira Product Discovery Additional Sandbox'
	) {
		// Additional Sandbox products have Cloud Premium and Cloud Enterprise
		return [
			{
				name: 'plan',
				label: 'Cloud Premium',
				value: 'Cloud Premium',
			},
			{
				name: 'plan',
				label: 'Cloud Enterprise',
				value: 'Cloud Enterprise',
			},
		];
	} else if (selectedProduct === 'DX') {
		// Only available for Premium & Enterprise
		return [
			{
				name: 'plan',
				label: 'Cloud Premium',
				value: 'Cloud Premium',
			},
			{
				name: 'plan',
				label: 'Cloud Enterprise',
				value: 'Cloud Enterprise',
			},
		];
	} else {
		// Default plans for other products
		return [
			{
				name: 'plan',
				label: 'Cloud Standard',
				value: 'Cloud Standard',
			},
			{
				name: 'plan',
				label: 'Cloud Premium',
				value: 'Cloud Premium',
			},
			{
				name: 'plan',
				label: 'Cloud Enterprise',
				value: 'Cloud Enterprise',
			},
		];
	}
};
